// src/app/pricing/plans.ts

import { type PricingSection } from "~/types"

export interface Plan {
  name: string
  price: {
    monthly: string
    annually: string
  }
  description: string
  capacity: string[]
  features: string[]
  isStarter: boolean
  isRecommended: boolean
  buttonText: string
  buttonLink: string
}

export const plans: Plan[] = [
  {
    name: "Starter",
    price: { monthly: "$199", annually: "$159" },
    description:
      "For small teams and startups looking to optimize their ad performance.",
    capacity: ["1 user, 1 admin", "1 workspace"],
    features: [
      "25 minutes of video analysis/month",
      "$50,000 monthly ad spend analyzed",
      "Multi-channel platform integrations"
    ],
    isStarter: true,
    isRecommended: false,
    buttonText: "Get started",
    buttonLink: "/request-access"
  },
  {
    name: "Business",
    price: { monthly: "$499", annually: "$399" },
    description: "For larger teams looking to scale their creative performance.",
    capacity: ["Up to 5 users, 2 admins", "Up to 5 workspaces"],
    features: [
      "250 Minutes of video analysis/month",
      "$250,000 monthly ad spend analyzed",
      "1-year data retention",
      "Competitor analysis",
      "Advanced trend tracker"
    ],
    isStarter: false,
    isRecommended: true,
    buttonText: "Start 14-day trial",
    buttonLink: "/request-access"
  },
  {
    name: "Enterprise",
    price: { monthly: "Custom", annually: "Custom" },
    description: "For agencies and businesses that need more advanced controls and features.",
    capacity: ["Up to 500 users, 10 admins", "Unlimited workspaces"],
    features: [
      "Unlimited video analysis",
      "Unlimited ad spend analyzed",
      "Unlimited data retention",
      "Custom integrations",
      "Dedicated account manager"
    ],
    isStarter: false,
    isRecommended: false,
    buttonText: "Contact us",
    buttonLink: "/request-access"
  }
]


export const pricingSections: PricingSection[] = [
  {
    name: "Core Features",
    features: [
      {
        name: "AI-powered video analysis",
        tooltip: "Advanced AI analysis of video content, including visual and audio elements.",
        plans: { Starter: true, Business: true, Enterprise: true }
      },
      {
        name: "Performance insights dashboard",
        tooltip: "Comprehensive dashboard showcasing key performance metrics and trends.",
        plans: { Starter: true, Business: true, Enterprise: true }
      },
      {
        name: "Monthly ad spend analyzed",
        tooltip: "Total advertising budget our platform will analyze and provide insights on.",
        plans: {
          Starter: "$50,000",
          Business: "$250,000",
          Enterprise: "Unlimited/Customized¹"
        }
      },
      {
        name: "Videos analyzed (minutes/mo.)",
        tooltip: "Total duration of video content our AI will process and analyze monthly.",
        plans: {
          Starter: "25",
          // Must match the Business plan card's `features` entry above
          // ("250 Minutes of video analysis/month") and every external
          // surface (pricing.md, index.md, about.md, request-access.md,
          // llms-full.txt). This cell previously read "100", contradicting
          // the card on the same page and every AI-facing .md mirror.
          Business: "250",
          Enterprise: "Unlimited"
        }
      },
      {
        name: "Seats",
        tooltip: "Total number of users (including guests) in each workspace.",
        plans: {
          Starter: "1",
          Business: "5",
          Enterprise: "Unlimited"
        }
      }
    ]
  },
  {
    name: "Advanced Analytics",
    features: [
      {
        name: "Competitor analysis",
        tooltip: "In-depth analysis of competitor ad strategies and performance.",
        plans: { Starter: false, Business: false, Enterprise: true }
      },
      {
        name: "Trend Tracker",
        tooltip: "Tool to identify and track emerging trends in ad creative and performance.",
        plans: { Starter: false, Business: true, Enterprise: true }
      },
      {
        name: "Hook Grader",
        tooltip: "AI-powered tool to evaluate and score ad opening effectiveness.",
        plans: { Starter: false, Business: "Standard", Enterprise: "Advanced" }
      },
      {
        name: "Multi-Modal Embeddings",
        tooltip: "AI-powered embedding creation of video, audio, and text content for comprehensive insights.",
        plans: { Starter: false, Business: "First 3 seconds", Enterprise: "Full Video" }
      },
      {
        name: "Custom report builder",
        tooltip: "Tool to create tailored reports based on specific metrics and insights",
        plans: { Starter: false, Business: "Limited", Enterprise: "Advanced" }
      }
    ]
  },
  {
    name: "Analytics",
    features: [
      {
        name: "Analytics retention",
        tooltip: "Duration for which detailed analysis and performance data is stored.",
        plans: { Starter: "14 days", Business: "1 year", Enterprise: "Unlimited" }
      },
      {
        name: "Anomaly detection",
        tooltip: "Proactive detection of unusual or unexpected data patterns in ad performance.",
        plans: { Starter: false, Business: true, Enterprise: true }
      },
      {
        name: "Platform Integrations",
        tooltip: "Number of third-party platforms that can be connected for data syncing.",
        plans: { Starter: "2", Business: "5", Enterprise: "Unlimited" }
      }
    ]
  },
  {
    name: "Support",
    features: [
      {
        name: "Slack",
        plans: {
          Starter: "Community",
          Business: "Connect",
          Enterprise: "Dedicated agent"
        }
      },
      {
        name: "Email",
        plans: { Starter: "2-4 days", Business: "1-2 days", Enterprise: "Priority" }
      },
      {
        name: "Dedicated Account Manager",
        plans: { Starter: false, Business: false, Enterprise: true }
      },
      {
        name: "Training Sessions",
        plans: { Starter: false, Business: "1 session", Enterprise: "Unlimited" }
      }
    ]
  }
] as const